/**
 * RealisticDrivingPhysics - Realistische Fahrphysik für Open World
 * 
 * Simuliert:
 * - Unterschiedliche Straßenoberflächen (Asphalt, Kopfstein, Schotter)
 * - Steigungen und Gefälle
 * - Wetter-Einfluss auf Grip
 * - Reifenverschleiß simuliert
 * - Kraftstoffverbrauch
 */

import * as THREE from 'three';
import type { RoadType } from './OpenWorldMapLoader';
import type { WeatherType } from './OpenWorldManager';

// Straßenoberflächen-Typen
export type SurfaceType = 'asphalt' | 'cobblestone' | 'gravel' | 'dirt' | 'wet_asphalt' | 'snow' | 'ice';

// Oberflächeneigenschaften
interface SurfaceProperties {
  gripMultiplier: number;      // 0-1, wie viel Grip
  rollingResistance: number;   // Rollwiderstand
  bumpiness: number;           // Unebenheit (für Fahrzeugbewegung)
  maxSpeed: number;            // Max empfohlene Geschwindigkeit
  soundType: string;           // Fahrgeräusch
}

const SURFACE_PROPERTIES: Record<SurfaceType, SurfaceProperties> = {
  asphalt:       { gripMultiplier: 1.0,  rollingResistance: 0.01, bumpiness: 0.0,  maxSpeed: 250, soundType: 'smooth' },
  cobblestone:   { gripMultiplier: 0.85, rollingResistance: 0.03, bumpiness: 0.3,  maxSpeed: 60,  soundType: 'bumpy' },
  gravel:        { gripMultiplier: 0.6,  rollingResistance: 0.05, bumpiness: 0.2,  maxSpeed: 80,  soundType: 'gravel' },
  dirt:          { gripMultiplier: 0.5,  rollingResistance: 0.06, bumpiness: 0.25, maxSpeed: 70,  soundType: 'dirt' },
  wet_asphalt:   { gripMultiplier: 0.7,  rollingResistance: 0.02, bumpiness: 0.0,  maxSpeed: 200, soundType: 'wet' },
  snow:          { gripMultiplier: 0.4,  rollingResistance: 0.04, bumpiness: 0.1,  maxSpeed: 50,  soundType: 'snow' },
  ice:           { gripMultiplier: 0.15, rollingResistance: 0.01, bumpiness: 0.0,  maxSpeed: 30,  soundType: 'ice' }
};

// Straßentyp zu Oberfläche Mapping
const ROAD_SURFACE_MAP: Record<RoadType, SurfaceType> = {
  motorway: 'asphalt',
  trunk: 'asphalt',
  primary: 'asphalt',
  secondary: 'asphalt',
  tertiary: 'asphalt',
  residential: 'asphalt',
  service: 'asphalt',
  unclassified: 'gravel'
};

// Baden-Württemberg Höhendaten (vereinfacht - echte Daten würden von SRTM kommen)
// Region: Schwarzwald, Schwäbische Alb, Rheinebene
const BW_ELEVATION_REGIONS = {
  schwarzwald: { minLat: 47.6, maxLat: 48.5, minLon: 7.6, maxLon: 8.4, baseElevation: 400, maxElevation: 1493 },
  schwaebischeAlb: { minLat: 48.2, maxLat: 48.8, minLon: 8.8, maxLon: 10.2, baseElevation: 500, maxElevation: 1015 },
  rheinebene: { minLat: 47.5, maxLat: 49.5, minLon: 7.5, maxLon: 8.5, baseElevation: 100, maxElevation: 200 },
  bodensee: { minLat: 47.5, maxLat: 47.9, minLon: 8.9, maxLon: 9.8, baseElevation: 395, maxElevation: 450 },
  neckartal: { minLat: 48.5, maxLat: 49.5, minLon: 8.5, maxLon: 9.5, baseElevation: 200, maxElevation: 400 }
};

export interface DrivingState {
  currentSurface: SurfaceType;
  gripLevel: number;           // 0-1 aktueller Grip
  elevation: number;           // Höhe über NN in Metern
  gradient: number;            // Steigung in Prozent (-20 bis +20)
  tirewear: number;            // Reifenverschleiß 0-100%
  fuelLevel: number;           // Tank 0-100%
  engineTemp: number;          // Motortemperatur
  isSlipping: boolean;         // Durchdrehen der Räder
  isSkidding: boolean;         // Schleudern
  recommendedSpeed: number;    // Empfohlene Höchstgeschwindigkeit
}

export interface PhysicsModifiers {
  accelerationMod: number;
  brakingMod: number;
  steeringMod: number;
  maxSpeedMod: number;
  driftMod: number;
}

export class RealisticDrivingPhysics {
  private state: DrivingState = {
    currentSurface: 'asphalt',
    gripLevel: 1.0,
    elevation: 300,
    gradient: 0,
    tirewear: 0,
    fuelLevel: 100,
    engineTemp: 90,
    isSlipping: false,
    isSkidding: false,
    recommendedSpeed: 130
  };

  private currentWeather: WeatherType = 'sunny';
  private lastPosition = new THREE.Vector3();
  private lastElevation = 300;
  
  // Rausch-Generator für Terrain
  private terrainSeed = Math.random() * 1000;

  /**
   * Berechnet Höhe basierend auf GPS-Position in Baden-Württemberg
   */
  getElevationAt(lat: number, lon: number): number {
    let baseElevation = 250; // Standard für BW
    let maxElevation = 400;
    
    // Prüfe welche Region
    for (const [, region] of Object.entries(BW_ELEVATION_REGIONS)) {
      if (lat >= region.minLat && lat <= region.maxLat &&
          lon >= region.minLon && lon <= region.maxLon) {
        baseElevation = region.baseElevation;
        maxElevation = region.maxElevation;
        break;
      }
    }
    
    // Perlin-ähnlicher Noise für sanfte Hügel
    const noise1 = Math.sin(lat * 50 + this.terrainSeed) * Math.cos(lon * 50) * 0.5 + 0.5;
    const noise2 = Math.sin(lat * 200) * Math.cos(lon * 200) * 0.3;
    const noise3 = Math.sin(lat * 20 + lon * 20) * 0.2;
    
    const combined = (noise1 + noise2 + noise3) / 1.5;
    
    return baseElevation + combined * (maxElevation - baseElevation);
  }

  /**
   * Berechnet Steigung basierend auf Bewegungsrichtung
   */
  calculateGradient(currentPos: THREE.Vector3, currentElevation: number, dt: number): number {
    if (dt <= 0) return 0;
    
    const horizontalDist = new THREE.Vector2(
      currentPos.x - this.lastPosition.x,
      currentPos.z - this.lastPosition.z
    ).length();
    
    if (horizontalDist < 0.1) return this.state.gradient; // Zu kleine Bewegung
    
    const elevationChange = currentElevation - this.lastElevation;
    const gradientPercent = (elevationChange / horizontalDist) * 100;
    
    // Glätten
    const smoothing = 0.1;
    return this.state.gradient * (1 - smoothing) + gradientPercent * smoothing;
  }

  /**
   * Ermittelt Oberfläche basierend auf Straßentyp und Wetter
   */
  determineSurface(roadType: RoadType | undefined, weather: WeatherType): SurfaceType {
    // Basis-Oberfläche
    let surface: SurfaceType = roadType ? ROAD_SURFACE_MAP[roadType] : 'dirt';
    
    // Wetter-Modifikation
    switch (weather) {
      case 'rainy':
        if (surface === 'asphalt') surface = 'wet_asphalt';
        if (surface === 'dirt') surface = 'dirt'; // Matsch würde noch schlechter
        break;
      case 'snowy':
        surface = 'snow';
        break;
      case 'foggy':
        // Nebel = oft feucht
        if (surface === 'asphalt' && Math.random() > 0.5) surface = 'wet_asphalt';
        break;
    }
    
    return surface;
  }

  /**
   * Berechnet Grip-Level
   */
  calculateGrip(surface: SurfaceType, speed: number, tirewear: number): number {
    const surfaceProps = SURFACE_PROPERTIES[surface];
    let grip = surfaceProps.gripMultiplier;
    
    // Reifenverschleiß reduziert Grip
    grip *= 1 - (tirewear / 100) * 0.3;
    
    // Hohe Geschwindigkeit auf schlechten Oberflächen = weniger Grip
    if (speed > surfaceProps.maxSpeed) {
      const overspeed = speed - surfaceProps.maxSpeed;
      grip *= Math.max(0.3, 1 - overspeed * 0.005);
    }
    
    return Math.max(0.1, Math.min(1.0, grip));
  }

  /**
   * Simuliert Reifenverschleiß
   */
  updateTireWear(dt: number, speed: number, isSlipping: boolean, isDrifting: boolean): number {
    let wearRate = 0;
    
    // Basis-Verschleiß durch Fahren
    wearRate += speed * 0.00001 * dt;
    
    // Erhöhter Verschleiß bei Durchdrehen
    if (isSlipping) wearRate += 0.01 * dt;
    
    // Erhöhter Verschleiß beim Driften
    if (isDrifting) wearRate += 0.02 * dt;
    
    return Math.min(100, this.state.tirewear + wearRate);
  }

  /**
   * Simuliert Kraftstoffverbrauch
   */
  updateFuel(dt: number, speed: number, gradient: number, throttle: number): number {
    // Basis-Verbrauch: höher bei Vollgas und Steigung
    let consumption = 0.001 * dt; // Leerlauf
    
    consumption += throttle * 0.01 * dt; // Beschleunigung
    consumption += Math.abs(speed) * 0.0001 * dt; // Geschwindigkeit
    
    // Bergauf = mehr Verbrauch
    if (gradient > 0) {
      consumption *= 1 + gradient * 0.02;
    }
    
    return Math.max(0, this.state.fuelLevel - consumption);
  }

  /**
   * Prüft auf Schlupf und Schleudern
   */
  checkSlippage(
    speed: number, 
    steeringAngle: number, 
    throttle: number, 
    grip: number
  ): { isSlipping: boolean; isSkidding: boolean } {
    const isSlipping = throttle > 0.8 && grip < 0.5 && speed < 30;
    const isSkidding = Math.abs(steeringAngle) > 0.3 && speed > 50 && grip < 0.6;
    
    return { isSlipping, isSkidding };
  }

  /**
   * Berechnet Fahrzeugbewegung durch Unebenheiten
   */
  calculateBumpMotion(surface: SurfaceType, speed: number, time: number): THREE.Vector3 {
    const bumpiness = SURFACE_PROPERTIES[surface].bumpiness;
    if (bumpiness === 0) return new THREE.Vector3();
    
    const intensity = bumpiness * Math.min(1, speed / 50);
    
    return new THREE.Vector3(
      Math.sin(time * 20) * intensity * 0.02,
      Math.sin(time * 30 + 1.5) * intensity * 0.03,
      Math.cos(time * 25) * intensity * 0.02
    );
  }

  /**
   * Haupt-Update - berechnet alle Physik-Modifikatoren
   */
  update(
    dt: number,
    position: THREE.Vector3,
    gpsLat: number,
    gpsLon: number,
    speed: number,
    roadType: RoadType | undefined,
    weather: WeatherType,
    throttle: number,
    steeringAngle: number,
    isDrifting: boolean
  ): PhysicsModifiers {
    // Höhe und Steigung
    const elevation = this.getElevationAt(gpsLat, gpsLon);
    const gradient = this.calculateGradient(position, elevation, dt);
    
    // Oberfläche
    const surface = this.determineSurface(roadType, weather);
    
    // Grip
    const grip = this.calculateGrip(surface, speed, this.state.tirewear);
    
    // Schlupf
    const { isSlipping, isSkidding } = this.checkSlippage(speed, steeringAngle, throttle, grip);
    
    // Verschleiß und Kraftstoff
    const tirewear = this.updateTireWear(dt, speed, isSlipping, isDrifting);
    const fuelLevel = this.updateFuel(dt, speed, gradient, throttle);
    
    // Empfohlene Geschwindigkeit
    const surfaceProps = SURFACE_PROPERTIES[surface];
    let recommendedSpeed = surfaceProps.maxSpeed;
    
    // Steigung reduziert empfohlene Geschwindigkeit
    if (gradient > 5) {
      recommendedSpeed *= 0.8;
    } else if (gradient < -5) {
      recommendedSpeed *= 0.9; // Bergab = etwas vorsichtiger
    }
    
    // State aktualisieren
    this.state = {
      currentSurface: surface,
      gripLevel: grip,
      elevation,
      gradient,
      tirewear,
      fuelLevel,
      engineTemp: 90 + throttle * 10, // Vereinfacht
      isSlipping,
      isSkidding,
      recommendedSpeed
    };
    
    this.lastPosition.copy(position);
    this.lastElevation = elevation;
    this.currentWeather = weather;
    
    // Physics Modifiers berechnen
    const gradientFactor = 1 - gradient * 0.01; // -1% pro % Steigung
    
    return {
      accelerationMod: grip * gradientFactor * (fuelLevel > 0 ? 1 : 0),
      brakingMod: grip * (gradient < 0 ? 1.2 : 1.0), // Bergab = bessere Bremswirkung
      steeringMod: grip * (isSkidding ? 0.5 : 1.0),
      maxSpeedMod: grip * (gradient > 0 ? gradientFactor : 1.0),
      driftMod: 1 / Math.max(0.3, grip) // Weniger Grip = leichteres Driften
    };
  }

  /**
   * Gibt aktuellen Zustand zurück
   */
  getState(): DrivingState {
    return { ...this.state };
  }

  /**
   * Setzt Reifen zurück (Boxenstopp)
   */
  resetTires(): void {
    this.state.tirewear = 0;
  }

  /**
   * Tankt auf
   */
  refuel(): void {
    this.state.fuelLevel = 100;
  }

  /**
   * Gibt Oberflächeneigenschaften zurück
   */
  getSurfaceProperties(surface: SurfaceType): SurfaceProperties {
    return { ...SURFACE_PROPERTIES[surface] };
  }
}

export default RealisticDrivingPhysics;
